import { forwardRef, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import IconCheck from "../icons/icon-check";

interface SelectOption {
	value: string;
	label: string;
}

interface SelectProps {
	label?: string;
	options: SelectOption[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	error?: string;
	className?: string;
	variant?: "light" | "dark";
	id?: string;
}

const Select = forwardRef<HTMLButtonElement, SelectProps>(({ label, options, value, onChange, placeholder = "Select...", disabled = false, error, className = "", variant = "light", id }, ref) => {
	const [open, setOpen] = useState(false);
	const selectedOption = options.find((opt) => opt.value === value);
	const isDark = variant === "dark";

	const handleSelect = (optionValue: string) => {
		if (disabled) return;
		onChange(optionValue);
		setOpen(false);
	};

	return (
		<div className={className}>
			{label && <label htmlFor={id} className="block text-sm font-medium text-primary mb-1.5">{label}</label>}
			<DropdownMenu.Root open={disabled ? false : open} onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}>
				<DropdownMenu.Trigger asChild>
					<button
						ref={ref}
						id={id}
						type="button"
						disabled={disabled}
						onClick={(e) => {
							if (disabled) {
								e.preventDefault();
								e.stopPropagation();
							}
						}}
						className={`flex items-center justify-between w-full px-4 py-2 border rounded-lg text-sm font-sans transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
							isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
						} ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${error ? "border-red-500" : ""} focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1`}
					>
						<span className={`flex-1 min-w-0 truncate text-left ${selectedOption ? (isDark ? "text-white" : "text-primary") : (isDark ? "text-gray-500" : "text-gray-400")}`}>{selectedOption ? selectedOption.label : placeholder}</span>
						<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className={`shrink-0 ml-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
							<path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
					</button>
				</DropdownMenu.Trigger>
				<DropdownMenu.Portal>
					<DropdownMenu.Content className={`rounded-lg shadow-lg border py-1 z-50 min-w-(--radix-dropdown-menu-trigger-width) ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`} sideOffset={4} align="start">
						{options.map((option) => (
							<DropdownMenu.Item
								key={option.value}
								className={`flex items-center justify-between px-4 py-2 text-sm cursor-pointer outline-none ${isDark ? "hover:bg-gray-700 data-highlighted:bg-gray-700" : "hover:bg-gray-50 data-highlighted:bg-gray-50"}`}
								onSelect={(e) => {
									e.preventDefault();
									handleSelect(option.value);
								}}
							>
								<span className={isDark ? "text-white" : "text-primary"}>{option.label}</span>
								{value === option.value && (
									<span className="ml-2">
										<IconCheck className="w-4 h-4 text-brand" />
									</span>
								)}
							</DropdownMenu.Item>
						))}
					</DropdownMenu.Content>
				</DropdownMenu.Portal>
			</DropdownMenu.Root>
			{error && <p className="text-sm text-red-500 mt-1">{error}</p>}
		</div>
	);
});

Select.displayName = "Select";

export default Select;
